import { useEffect, useSyncExternalStore } from "react";
import { markDiscoveryVisited } from "@/lib/depthPushTransition";

/**
 * Discovery TabBar spinner — single module for the whole protocol.
 *
 * Why it exists (do not delete without measurement): long tasks during Discovery
 * mount can block paint; a deferred spinner never appears. Pending is therefore
 * set synchronously on Discover click, TabBar yields two rAFs so the spinner
 * paints, then navigation runs inside startTransition.
 *
 * Call-site roles (thin reporters only):
 * - TabBar → {@link navigateToDiscoveryTab} / {@link leaveDiscoveryTab}
 * - `/` route → {@link useDiscoveryIndexRouteLifecycle}
 * - HomePage → {@link reportDiscoveryFeedSettled}
 * - VenueAlphaSection → {@link armDiscoveryEagerImages}
 * - EventImage → {@link markDiscoveryEagerImageDone}
 */

const SAFETY_MS = 8_000;

let pending = false;
let dataSettled = false;
/** True until A–Z (or HomePage fallback) arms the eager-image gate. */
let awaitingEagerArm = false;
let eagerImagesExpected = 0;
let eagerImagesLoaded = 0;
let eagerGateArmed = false;
let safetyTimer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

function clearSafetyTimer() {
  if (safetyTimer == null) return;
  clearTimeout(safetyTimer);
  safetyTimer = null;
}

function resetGates() {
  dataSettled = false;
  awaitingEagerArm = false;
  eagerImagesExpected = 0;
  eagerImagesLoaded = 0;
  eagerGateArmed = false;
  clearSafetyTimer();
}

function tryEnd() {
  if (!pending) return;
  if (!dataSettled) return;
  if (awaitingEagerArm) return;
  if (eagerGateArmed && eagerImagesLoaded < eagerImagesExpected) return;
  pending = false;
  resetGates();
  emit();
}

function beginDiscoveryNavigation() {
  if (pending) return;
  pending = true;
  resetGates();
  awaitingEagerArm = true;
  safetyTimer = setTimeout(() => {
    if (!pending) return;
    pending = false;
    resetGates();
    emit();
  }, SAFETY_MS);
  emit();
}

function endDiscoveryNavigation() {
  if (!pending) return;
  pending = false;
  resetGates();
  emit();
}

/** Yield two animation frames so the TabBar spinner can paint before route work. */
function afterDiscoverySpinnerPaint(run: () => void): void {
  if (typeof window === "undefined") {
    run();
    return;
  }
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      run();
    });
  });
}

/** TabBar Discover click: arm spinner, paint, then navigate. */
export function navigateToDiscoveryTab(runNavigation: () => void): void {
  beginDiscoveryNavigation();
  afterDiscoverySpinnerPaint(runNavigation);
}

/** TabBar leaving Discover: clear spinner state. */
export function leaveDiscoveryTab(): void {
  endDiscoveryNavigation();
}

/** `/` route mount: keep spinner protocol armed for the page lifetime. */
export function useDiscoveryIndexRouteLifecycle(): void {
  useEffect(() => {
    markDiscoveryVisited();
    beginDiscoveryNavigation();
    return () => endDiscoveryNavigation();
  }, []);
}

/** Suspense fallback for `/` — idempotent if route lifecycle already began. */
export function ensureDiscoveryNavigationPending(): void {
  beginDiscoveryNavigation();
}

/**
 * HomePage: initial venues + events settled.
 * Pass `armEagerImageGate: false` when the A–Z rail is not shown so dataSettled
 * alone can clear the spinner.
 */
export function reportDiscoveryFeedSettled(options: { armEagerImageGate: boolean }): void {
  if (!pending) return;
  dataSettled = true;
  if (!options.armEagerImageGate) {
    awaitingEagerArm = false;
    eagerGateArmed = true;
    eagerImagesExpected = 0;
    eagerImagesLoaded = 0;
    tryEnd();
    return;
  }
  tryEnd();
}

/**
 * Arm the eager-image gate (VenueAlphaSection). Pass 0 when the rail is empty
 * so dataSettled alone can clear the spinner.
 */
export function armDiscoveryEagerImages(count: number) {
  if (!pending) return;
  awaitingEagerArm = false;
  eagerGateArmed = true;
  eagerImagesExpected = Math.max(0, count);
  // Keep progress if re-armed with the same or higher budget mid-flight.
  eagerImagesLoaded = Math.min(eagerImagesLoaded, eagerImagesExpected);
  if (eagerImagesExpected === 0) {
    tryEnd();
    return;
  }
  emit();
  tryEnd();
}

/** One eager venue image finished (load or error). */
export function markDiscoveryEagerImageDone() {
  if (!pending || !eagerGateArmed) return;
  if (eagerImagesLoaded >= eagerImagesExpected) return;
  eagerImagesLoaded += 1;
  tryEnd();
}

function isDiscoveryNavigationPending(): boolean {
  return pending;
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Spinner tracks pending immediately (no deferred delay). */
export function useDiscoverySpinnerVisible(): boolean {
  return useSyncExternalStore(subscribe, isDiscoveryNavigationPending, () => false);
}
